import { clientPath, filePath, read, command } from './vault.http'
import { categoriesFor, exactFinancialYear, isUuid } from './sourceReviews'

export const HISTORICAL_TERMS = 'historical-document-portability.v1'
export interface Metadata {
  classification: 'client_controlled' | 'client_output'
  service: 'income_tax' | 'gst' | 'finance'
  category: string
  financial_year: string | null
}
export interface HistoricalCandidate {
  filename: string
  source_firm_id: string
  source_alias_id: string
  source_document_id: string
  source_version_id: string | null
  manifest_run_id: string
  observation_sha256: string
  content_sha256: string
  byte_size: number
  metadata: Metadata
}
export interface CandidatePage {
  client_id: string
  items: HistoricalCandidate[]
  page: number
  page_size: number
  has_more: boolean
  grants_copy_authority: false
}
export interface ConsentStatus {
  client_id: string
  consent_id: string
  source_firm_id: string
  source_alias_id: string
  source_document_id: string
  manifest_run_id: string
  observation_sha256: string
  metadata: Metadata
  terms_version: string
  recorded_at: string
  expires_at: string
  state: 'active' | 'expired' | 'withdrawn' | 'withdrawal_pending'
  withdrawal_id: string | null
  pending_operation_id: string | null
}
export interface ConsentPage {
  client_id: string
  items: ConsentStatus[]
  page: number
  page_size: number
  has_more: boolean
}
export interface ConsentCommand extends Omit<HistoricalCandidate, 'filename'> {
  terms_acknowledged: true
  terms_version: typeof HISTORICAL_TERMS
  idempotency_key: string
}
export type PendingCommand = {
  version: 1
  client: string
  key: string
  operation: 'consent' | 'withdrawal'
  source: string
  run: string
  consent: string | null
  fingerprint: string
}
const hex = (value: unknown) => typeof value === 'string' && /^[a-f0-9]{64}$/.test(value)
const date = (value: unknown) => typeof value === 'string' && Number.isFinite(Date.parse(value))
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)
function metadata(value: unknown): value is Metadata {
  if (
    !record(value) ||
    Object.keys(value).length !== 4 ||
    !Object.keys(value).every(key =>
      ['classification', 'service', 'category', 'financial_year'].includes(key)
    )
  )
    return false
  return (
    ['client_controlled', 'client_output'].includes(String(value.classification)) &&
    ['income_tax', 'gst', 'finance'].includes(String(value.service)) &&
    categoriesFor(value.classification as Metadata['classification']).includes(
      String(value.category)
    ) &&
    (value.financial_year === null || exactFinancialYear(String(value.financial_year)))
  )
}
function source(value: Record<string, unknown>) {
  return (
    ['source_firm_id', 'source_alias_id', 'source_document_id', 'manifest_run_id'].every(
      key => typeof value[key] === 'string' && isUuid(value[key] as string)
    ) &&
    hex(value.observation_sha256) &&
    metadata(value.metadata)
  )
}
export function validStatus(value: unknown, client: string): value is ConsentStatus {
  if (
    !record(value) ||
    value.client_id !== client ||
    !source(value) ||
    typeof value.consent_id !== 'string' ||
    !isUuid(value.consent_id) ||
    value.terms_version !== HISTORICAL_TERMS ||
    !date(value.recorded_at) ||
    !date(value.expires_at) ||
    !['active', 'expired', 'withdrawn', 'withdrawal_pending'].includes(String(value.state)) ||
    !(
      value.withdrawal_id === null ||
      (typeof value.withdrawal_id === 'string' && isUuid(value.withdrawal_id))
    ) ||
    !(
      value.pending_operation_id === null ||
      (typeof value.pending_operation_id === 'string' && isUuid(value.pending_operation_id))
    )
  )
    return false
  return value.state === 'withdrawal_pending'
    ? !!value.withdrawal_id && !!value.pending_operation_id
    : value.state === 'withdrawn'
      ? !!value.withdrawal_id && value.pending_operation_id === null
      : value.withdrawal_id === null
}
export const historicalPath = (client: string) => clientPath(client) + '/historical-documents'
export async function candidatePage(client: string, page: number): Promise<CandidatePage> {
  const value = await read<unknown>(filePath(client) + '/historical-candidates', {
    page,
    page_size: 20,
  })
  if (
    !record(value) ||
    value.client_id !== client ||
    value.page !== page ||
    value.page_size !== 20 ||
    typeof value.has_more !== 'boolean' ||
    value.grants_copy_authority !== false ||
    !Array.isArray(value.items) ||
    value.items.length > 20 ||
    !value.items.every(
      item =>
        record(item) &&
        Object.keys(item).length === 10 &&
        Object.keys(item).every(key =>
          [
            'filename',
            'source_firm_id',
            'source_alias_id',
            'source_document_id',
            'source_version_id',
            'manifest_run_id',
            'observation_sha256',
            'content_sha256',
            'byte_size',
            'metadata',
          ].includes(key)
        ) &&
        source(item) &&
        typeof item.filename === 'string' &&
        item.filename.length > 0 &&
        item.filename.length <= 255 &&
        (item.source_version_id === null ||
          (typeof item.source_version_id === 'string' && isUuid(item.source_version_id))) &&
        hex(item.content_sha256) &&
        Number.isSafeInteger(item.byte_size) &&
        Number(item.byte_size) > 0 &&
        Number(item.byte_size) <= 67108864
    ) ||
    new Set(value.items.map(item => item.source_document_id)).size !== value.items.length
  )
    throw new Error('Historical document choices could not be verified. Refresh the list.')
  return value as unknown as CandidatePage
}
export async function consentPage(client: string, page: number): Promise<ConsentPage> {
  const value = await read<unknown>(historicalPath(client) + '/consents', { page, page_size: 20 })
  if (
    !record(value) ||
    value.client_id !== client ||
    value.page !== page ||
    value.page_size !== 20 ||
    typeof value.has_more !== 'boolean' ||
    !Array.isArray(value.items) ||
    value.items.length > 20 ||
    !value.items.every(item => validStatus(item, client)) ||
    new Set(value.items.map(item => item.consent_id)).size !== value.items.length
  )
    throw new Error('Current historical consent status could not be verified.')
  return value as unknown as ConsentPage
}
export function consentBody(item: HistoricalCandidate, key: string): ConsentCommand {
  return {
    source_firm_id: item.source_firm_id,
    source_alias_id: item.source_alias_id,
    source_document_id: item.source_document_id,
    source_version_id: item.source_version_id,
    manifest_run_id: item.manifest_run_id,
    observation_sha256: item.observation_sha256,
    content_sha256: item.content_sha256,
    byte_size: item.byte_size,
    metadata: { ...item.metadata },
    terms_acknowledged: true,
    terms_version: HISTORICAL_TERMS,
    idempotency_key: key,
  }
}
export async function submitConsent(client: string, body: ConsentCommand) {
  const receipt = await command<Record<string, unknown>>(historicalPath(client) + '/consents', body)
  if (
    !receipt ||
    !isUuid(String(receipt.consent_id)) ||
    receipt.client_id !== client ||
    receipt.source_document_id !== body.source_document_id ||
    receipt.source_alias_id !== body.source_alias_id ||
    receipt.source_firm_id !== body.source_firm_id ||
    receipt.observation_sha256 !== body.observation_sha256 ||
    receipt.terms_version !== HISTORICAL_TERMS ||
    !date(receipt.expires_at)
  )
    throw new Error('The consent outcome is not confirmed. Keep the original request pending.')
}
export async function submitWithdrawal(client: string, consent: string, key: string) {
  if (!isUuid(consent)) throw new Error('A valid original consent is required')
  const result = await command<Record<string, unknown>>(
    historicalPath(client) + `/consents/${consent}:withdraw`,
    { idempotency_key: key }
  )
  if (
    !result ||
    result.client_id !== client ||
    result.consent_id !== consent ||
    !isUuid(String(result.withdrawal_id)) ||
    !['effective', 'pending_copy_outcome'].includes(String(result.state)) ||
    (result.state === 'effective'
      ? result.pending_operation_id !== null
      : !isUuid(String(result.pending_operation_id)))
  )
    throw new Error('Withdrawal is awaiting confirmation. Keep the original request pending.')
}
export async function recoverCommand(pending: PendingCommand): Promise<ConsentStatus | null> {
  const value = await read<Record<string, unknown>>(
    historicalPath(pending.client) + `/commands/${pending.key}`,
    { operation: pending.operation }
  )
  return commandOutcome(value, pending)
}
export async function cancelUnrecorded(pending: PendingCommand): Promise<ConsentStatus | null> {
  if (pending.operation !== 'consent') throw new Error('A recorded consent must use withdrawal.')
  const value = await command<Record<string, unknown>>(
    historicalPath(pending.client) + `/commands/${pending.key}:cancel`,
    {}
  )
  return commandOutcome(value, pending)
}
function commandOutcome(value: unknown, pending: PendingCommand): ConsentStatus | null {
  if (
    !record(value) ||
    value.client_id !== pending.client ||
    value.idempotency_key !== pending.key ||
    value.operation !== pending.operation
  )
    throw new Error('The original command outcome could not be verified.')
  if (
    value.outcome === 'cancelled_unrecorded' &&
    pending.operation === 'consent' &&
    value.status === null
  )
    return null
  if (
    (value.outcome !== undefined && value.outcome !== 'recorded') ||
    !validStatus(value.status, pending.client) ||
    value.status.source_document_id !== pending.source ||
    value.status.manifest_run_id !== pending.run ||
    (pending.consent !== null && value.status.consent_id !== pending.consent)
  )
    throw new Error('The original command outcome could not be verified.')
  return value.status
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return '[' + value.map(canonical).join(',') + ']'
  if (record(value))
    return (
      '{' +
      Object.keys(value)
        .sort()
        .map(key => JSON.stringify(key) + ':' + canonical(value[key]))
        .join(',') +
      '}'
    )
  return JSON.stringify(value)
}
export async function fingerprint(body: unknown): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(canonical(body)))
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('')
}
export function restorePending(storageKey: string, client: string): PendingCommand | null {
  try {
    const value: unknown = JSON.parse(sessionStorage.getItem(storageKey) ?? 'null')
    if (
      !record(value) ||
      value.version !== 1 ||
      value.client !== client ||
      !hex(value.fingerprint) ||
      !['consent', 'withdrawal'].includes(String(value.operation)) ||
      !['key', 'source', 'run'].every(
        k => typeof value[k] === 'string' && isUuid(value[k] as string)
      ) ||
      (value.operation === 'consent'
        ? value.consent !== null
        : typeof value.consent !== 'string' || !isUuid(value.consent)) ||
      Object.keys(value).some(
        k =>
          ![
            'version',
            'client',
            'key',
            'operation',
            'source',
            'run',
            'consent',
            'fingerprint',
          ].includes(k)
      )
    )
      return null
    return value as PendingCommand
  } catch {
    return null
  }
}
